// A shared forward-auth reverse proxy (traefik-forward-auth) sits in front of this app
// in staging and guards `*.toptal.tech` with Google SSO. When its own session expires,
// it 302-redirects requests toward Google and mints a fresh single-use CSRF cookie
// (`_forward_auth_toptal_tech_csrf_staging_<random>`) on every redirect, never clearing
// the old ones. Background fetches (the 30s Better-Auth session poll, react-query
// refetches) can never complete that top-level redirect, so each one silently mints
// another cookie. They accumulate (~2/min) until the `.toptal.tech` Cookie header
// exceeds nginx's buffer and nginx hard-blocks EVERY request with a 400 "Request Header
// Or Cookie Too Large" — before the request even reaches the app.
//
// PREVENTION ONLY: the real fix is infra-side (short CSRF Max-Age / clear-on-completion,
// and a larger nginx header buffer). We can't clear the cookies here — they're HttpOnly
// — and once nginx is 400-ing, a reload just re-sends the oversized header and blocks
// again. So this recovers while the header is still under the limit: detect the SSO
// redirect early and do one top-level `window.location.reload()`, which completes the
// handshake with the live Google session and stops the poll from minting further. It
// keeps users under nginx's limit; it cannot rescue a user already stuck at the 400.

// Same-origin GET used to positively confirm a proxy redirect. We probe the app ROOT
// (not /api/health): forward-auth guards the whole vhost, and a health endpoint is the
// path most likely to be whitelisted from auth (k8s liveness) — which would return a
// healthy 200 while real requests get redirected, defeating detection.
const authProbePath = () => '/';

const LATCH_KEY = 'app.authRecovery';
const RELOAD_COOLDOWN_MS = 60_000; // never reload the same tab more than once per minute
const MAX_RELOADS = 5; // hard backstop against a pathological reload loop

// Positive, CORS-safe evidence that the proxy redirected a request to SSO instead of
// reaching the JSON backend: a JSON /api call should never be redirected, and a
// `redirect: 'manual'` request turns a 3xx into an opaque redirect response.
export function responseIsProxyRedirect(res: Response): boolean {
  return res.redirected || res.type === 'opaqueredirect';
}

type Latch = { at: number; count: number };

function readLatch(): Latch {
  try {
    const raw = sessionStorage.getItem(LATCH_KEY);
    if (raw) return JSON.parse(raw) as Latch;
  } catch {
    // sessionStorage unavailable / malformed — treat as no prior reload
  }
  return { at: 0, count: 0 };
}

function writeLatch(latch: Latch): void {
  try {
    sessionStorage.setItem(LATCH_KEY, JSON.stringify(latch));
  } catch {
    // ignore — worst case we lose the cooldown, still bounded by MAX_RELOADS in-memory
  }
}

// Call on any successful JSON response: the session is healthy again, so reset the
// backstop so a future expiry is free to reload.
export function clearAuthRecoveryLatch(): void {
  try {
    sessionStorage.removeItem(LATCH_KEY);
  } catch {
    // ignore
  }
}

// With `redirect: 'manual'` the browser suppresses following a 3xx and surfaces it as
// an opaque redirect (type 'opaqueredirect', status 0) rather than blocking it as a
// cross-origin CORS error. That makes the proxy redirect unambiguously detectable,
// distinct from a genuine network failure (which rejects) or a reachable backend
// (which returns a normal response). Concurrent callers share one in-flight probe so
// a burst of failing requests doesn't fan out into a burst of probes.
let probeInFlight: Promise<boolean> | null = null;

function probeProxyRedirect(): Promise<boolean> {
  if (!probeInFlight) {
    probeInFlight = (async () => {
      try {
        const res = await fetch(authProbePath(), {
          method: 'GET',
          credentials: 'include',
          redirect: 'manual',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        return res.type === 'opaqueredirect';
      } catch {
        return false; // genuine network error — do not reload, let normal handling proceed
      } finally {
        probeInFlight = null;
      }
    })();
  }
  return probeInFlight;
}

// Recover from a suspected SSO expiry by reloading the tab once. Pass `confirmed:
// true` when the caller already holds positive evidence (a redirected response);
// otherwise we run the manual-redirect probe so a plain network blip never reloads
// the tab (and never discards unsaved input on a transient hiccup).
export async function recoverFromAuthRedirect(confirmed: boolean): Promise<void> {
  if (typeof window === 'undefined') return;
  if (!confirmed && !(await probeProxyRedirect())) return;

  const latch = readLatch();
  const now = Date.now();
  if (latch.count >= MAX_RELOADS) return; // pathological loop backstop
  if (now - latch.at < RELOAD_COOLDOWN_MS) return; // already reloaded very recently

  writeLatch({ at: now, count: latch.count + 1 });
  window.location.reload();
}
